import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { HiOutlinePhoto, HiOutlineSparkles } from "react-icons/hi2";
import { FiX, FiPlus, FiInfo } from "react-icons/fi";
import hazardReportService from "../services/hazardReportService";
import authService from "../services/authService";
import plantService from "../services/plantService";
import masterDataService from "../services/masterDataService";
import siteLayoutService from "../services/siteLayoutService";
import hazardCategoryService from "../services/hazardCategoryService";
import { aiService } from "../services/aiService";
import { toast } from "react-toastify";
import PageLoading from "../components/common/PageLoading";
import "./HazardReport.css";

const HazardReportForm = ({
  isOpen: propIsOpen,
  onClose: propOnClose,
  reportId: propReportId,
  aiPrefillObject,
  aiPrefillFiles,
}) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const location = useLocation();
  
  // Determine if we are on a standalone route (/hazard-reports/new or /hazard-reports/edit/:id)
  const isNewRoute = location.pathname.includes("/hazard-reports/new");
  const isEditRoute = location.pathname.includes("/hazard-reports/edit/");
  const isStandalonePage = isNewRoute || isEditRoute;
  
  // Effectively merge props and params
  const isOpen = propIsOpen || isStandalonePage;
  const reportId = propReportId || (isEditRoute ? paramId : null);
  const onClose = () => {
    if (propOnClose) {
      propOnClose();
    } else if (isStandalonePage) {
      navigate("/home/hazard-reports");
    }
  };

  // Toast replaced
  const tenantId = authService.getTenantId();
  const currentUser = authService.getUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Master data
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [severityLevels, setSeverityLevels] = useState([]);
  const [siteLayout, setSiteLayout] = useState(null);
  const [layoutItems, setLayoutItems] = useState({});
  const [selectedLayout, setSelectedLayout] = useState({});
  const [settings, setSettings] = useState({
    facilityLevel: 1,
    enableSeverity: true,
    enableImmediateAction: true,
    enableImmediateDangerTracking: true,
    requireEvidence: false,
    enableApproval: false,
  });

  const getInitialFormData = () => ({
    title: "",
    reporterName: (
      currentUser?.fullName ||
      (currentUser?.firstName
        ? `${currentUser.firstName} ${currentUser.lastName || ""}`.trim()
        : currentUser?.username) ||
      ""
    ),
    reportedByUserId: currentUser?.userId || "",
    departmentId: "",
    reportedAtDate: "",
    reportedAtTime: "",
    location: "",
    plantId: "",
    description: "",
    categoryId: "",
    isImmediateDanger: false,
    severityLevelId: "",
    immediateActionTaken: "",
    approverRoleId: "",
    status: "Open",
    referenceNumber: "",
    attachments: [],
    filesToUpload: [],
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const isEditMode = Boolean(reportId);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiSuggested, setAiSuggested] = useState({});

  // Removed handleAiAnalyze as it has been moved to the list page

  useEffect(() => {
    if (aiPrefillObject && !loading && !isEditMode) {
      setFormData((prev) => {
        const updates = { ...prev };
        const suggested = {};

        if (aiPrefillFiles && aiPrefillFiles.length > 0) {
          const newFiles = Array.from(aiPrefillFiles).filter(
            (f) => !prev.filesToUpload.includes(f),
          );
          if (newFiles.length > 0) {
            updates.filesToUpload = [...prev.filesToUpload, ...newFiles];
          }
        }

        // Map Title
        const mappedTitle =
          aiPrefillObject.hazardTitle || aiPrefillObject.hazard_title;
        if (mappedTitle) {
          updates.title = mappedTitle;
          suggested.title = true;
        }

        // Map Description
        const mappedDesc =
          aiPrefillObject.description ||
          aiPrefillObject.hazardDescription ||
          aiPrefillObject.hazard_description;
        if (mappedDesc) {
          updates.description = mappedDesc;
          suggested.description = true;
        }

        // Map Immediate Action
        const mappedAction =
          aiPrefillObject.recommendedAction ||
          aiPrefillObject.recommended_action ||
          aiPrefillObject.suggestedAction;
        if (mappedAction) {
          updates.immediateActionTaken = mappedAction;
          suggested.immediateActionTaken = true;
        }

        // Map Immediate Danger Flag
        const isDanger =
          aiPrefillObject.immediateDanger !== undefined
            ? aiPrefillObject.immediateDanger
            : aiPrefillObject.immediate_danger;
        if (isDanger !== undefined) {
          updates.isImmediateDanger = isDanger;
          suggested.isImmediateDanger = true;
        }

        // Fuzzy-match category
        const aiCategory =
          aiPrefillObject.hazardCategory ||
          aiPrefillObject.hazard_category ||
          aiPrefillObject.suggestedCategory;
        if (aiCategory && categories.length > 0) {
          const suggestedCat = aiCategory.toLowerCase();
          const match = categories.find((c) => {
            const name = (
              c.categoryName ||
              c.CategoryName ||
              c.name ||
              ""
            ).toLowerCase();
            if (!name) return false;
            const aiWords = suggestedCat
              .split(/[\s/_-]+/)
              .filter((w) => w.length >= 3);
            return (
              name === suggestedCat ||
              name.includes(suggestedCat) ||
              suggestedCat.includes(name) ||
              aiWords.some((w) => name.includes(w))
            );
          });

          if (match) {
            updates.categoryId = String(
              match.categoryId || match.CategoryId || match.id,
            );
            suggested.categoryId = true;
          } else if (categories.length > 0) {
            // Fallback to "Other", "General", or just the first item if no confident match
            const fallback =
              categories.find((c) => {
                const n = (c.categoryName || c.name || "").toLowerCase();
                return n.includes("other") || n.includes("general");
              }) || categories[0];

            updates.categoryId = String(
              fallback.categoryId || fallback.CategoryId || fallback.id,
            );
            suggested.categoryId = true;
          }
        }

        // Map severity
        const aiSeverity =
          aiPrefillObject.severity || aiPrefillObject.riskLevel;
        if (aiSeverity && severityLevels.length > 0) {
          const riskMap = { low: 0, medium: 1, high: 2, critical: 3 };
          const riskIdx = riskMap[aiSeverity.toLowerCase()] ?? -1;
          if (riskIdx >= 0 && riskIdx < severityLevels.length) {
            const sev =
              severityLevels[Math.min(riskIdx, severityLevels.length - 1)];
            updates.severityLevelId = String(
              sev.severityLevelId || sev.SeverityLevelId || sev.id,
            );
            suggested.severityLevelId = true;
          }
        }

        setAiSuggested(suggested);
        return updates;
      });
      // toast.success("AI analysis loaded! Review the auto-filled fields."); // Optional, can remove so it doesn't double-toast
    }
  }, [
    aiPrefillObject,
    loading,
    isEditMode,
    categories,
    severityLevels,
    aiPrefillFiles,
  ]);

  useEffect(() => {
    if (isOpen) {
      if (!reportId) {
        setFormData(getInitialFormData());
        setAiSuggested({});
        setSelectedLayout({});
        setLoading(false); // New form loads fast initially
      }
      if (tenantId) {
        loadInitialData();
      } else {
        setLoading(false);
      }
    }
  }, [reportId, tenantId, isOpen]);

  useEffect(() => {
    if (formData.plantId) {
      fetchSiteLayout(formData.plantId);
    } else {
      setSiteLayout(null);
      setLayoutItems({});
      setSelectedLayout({});
    }
  }, [formData.plantId]);

  const fetchSiteLayout = async (plantId) => {
    if (!plantId) return;
    try {
      const layoutData = await siteLayoutService.getSiteLayout(plantId);
      setSiteLayout(layoutData || null);

      if (layoutData && layoutData.level1) {
        const level1Items = await siteLayoutService.getLevelItems(plantId, 1);
        setLayoutItems(prev => ({ ...prev, 1: level1Items || [] }));
      } else {
        setLayoutItems({});
      }
    } catch (error) {
      console.error("Error fetching site layout:", error);
      setLayoutItems({});
    }
  };

  const initializeLayoutFromArea = async (plantId, areaName) => {
    if (!plantId || !areaName) return;

    try {
      let foundItem = null;
      let foundLevel = 0;

      const levels = [1, 2, 3, 4, 5, 6];
      const searchPromises = levels.map((level) =>
        siteLayoutService
          .searchLevelItems(plantId, level, areaName)
          .then((items) => ({ level, items: items || [] }))
          .catch(() => ({ level, items: [] })),
      );

      const results = await Promise.all(searchPromises);

      for (const res of results) {
        const match = res.items.find((i) => i.name === areaName);
        if (match) {
          foundItem = match;
          foundLevel = res.level;
          break;
        }
      }

      if (foundItem) {
        const newSelected = {};
        const newItems = {};

        newSelected[foundLevel] = foundItem.id;

        let curr = foundItem;
        let lvl = foundLevel;

        while (lvl > 1 && curr.parentId) {
          const parent = await siteLayoutService.getLevelItemById(curr.parentId);
          if (parent) {
            newSelected[lvl - 1] = parent.id;
            curr = parent;
            lvl--;
          } else {
            break;
          }
        }

        const fetchPromises = [];

        fetchPromises.push(
          siteLayoutService.getLevelItems(plantId, 1).then((items) => {
            newItems[1] = items || [];
          }),
        );

        for (let l = 2; l <= foundLevel; l++) {
          fetchPromises.push(
            siteLayoutService.getLevelItems(plantId, l).then((items) => {
              newItems[l] = items || [];
            }),
          );
        }

        if (foundLevel < 6) {
          fetchPromises.push(
            siteLayoutService.getLevelItems(plantId, foundLevel + 1).then((items) => {
              newItems[foundLevel + 1] = items || [];
            }),
          );
        }

        await Promise.all(fetchPromises);

        setLayoutItems((prev) => ({ ...prev, ...newItems }));
        setSelectedLayout(newSelected);
      }
    } catch (error) {
      console.error("Error restoring layout:", error);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);

      const [
        plantsData,
        departmentsData,
        rolesData,
        categoriesData,
        severityData,
        settingsData,
      ] = await Promise.all([
        plantService.getAllPlants().catch(() => []),
        masterDataService.getDepartments(tenantId).catch(() => []),
        masterDataService.getRoles(tenantId).catch(() => []),
        hazardCategoryService.getCategories().catch(() => []),
        masterDataService.getSeverityLevels(tenantId).catch(() => []),
        hazardReportService.getHazardSettings().catch(() => null),
      ]);

      setPlants(plantsData || []);

      // Auto-select plant if only 1 exists and not in edit mode
      let autoSelectedPlantId = "";
      if (!isEditMode && plantsData?.length === 1) {
        autoSelectedPlantId = plantsData[0].plantId.toString();
      }
      
      setDepartments(departmentsData || []);
      setRoles(rolesData || []);
      setCategories(categoriesData || []);
      setSeverityLevels(severityData || []);

      if (settingsData) {
        setSettings({
          facilityLevel:
            settingsData.facilityLevel ?? settingsData.FacilityLevel ?? 1,
          enableSeverity:
            settingsData.enableSeverity ?? settingsData.EnableSeverity ?? true,
          enableImmediateAction:
            settingsData.enableImmediateAction ??
            settingsData.EnableImmediateAction ??
            true,
          enableImmediateDangerTracking:
            settingsData.enableImmediateDangerTracking ??
            settingsData.EnableImmediateDangerTracking ??
            true,
          requireEvidence:
            settingsData.requireEvidence ??
            settingsData.RequireEvidence ??
            false,
          enableApproval:
            settingsData.enableApproval ?? settingsData.EnableApproval ?? false,
        });
      }

      if (isEditMode) {
        await loadHazardReport();
      } else {
        // Form is already reset at the start of useEffect, 
        // but we ensure reporter details are correct here if needed.
        setFormData(prev => ({
            ...getInitialFormData(),
            plantId: autoSelectedPlantId || prev.plantId
        }));
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      toast.error("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadHazardReport = async () => {
    try {
      const report = await hazardReportService.getHazardReportById(reportId);
      if (report) {
        const reportedAt = new Date(report.reportedAt);
        setFormData({
          title: report.title || "",
          reporterName: report.reporterName || "",
          reportedByUserId:
            report.reportedByUserId || currentUser?.userId || "",
          departmentId: report.departmentId?.toString() || "",
          reportedAtDate: reportedAt.toISOString().split("T")[0],
          reportedAtTime: reportedAt.toTimeString().slice(0, 5),
          location: report.location || "",
          plantId: report.plantId?.toString() || "",
          description: report.description || "",
          categoryId: report.categoryId?.toString() || "",
          isImmediateDanger: report.isImmediateDanger || false,
          severityLevelId: report.severityLevelId?.toString() || "",
          immediateActionTaken: report.immediateActionTaken || "",
          approverRoleId: report.approverRoleId?.toString() || "",
          status: report.status || "Open",
          referenceNumber: report.referenceNumber || "",
          attachments: report.attachments || [],
          filesToUpload: [],
        });

        // Initialize layout if plant and location exist
        const pId = report.plantId;
        const areaName = report.location;
        if (pId) {
          await fetchSiteLayout(pId);
          if (areaName && areaName !== "General Area") {
            await initializeLayoutFromArea(pId, areaName);
          }
        }
      }
    } catch (err) {
      console.error("Error loading hazard report:", err);
      toast.error("Failed to load hazard report.");
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === "plantId") {
      setLayoutItems({});
      setSelectedLayout({});
      setFormData((prev) => ({
        ...prev,
        location: "",
        [name]: value,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLayoutChange = async (level, itemId) => {
    const nextLevel = level + 1;
    const newSelected = { ...selectedLayout, [level]: itemId };
    for (let i = nextLevel; i <= 6; i++) {
      delete newSelected[i];
    }
    setSelectedLayout(newSelected);

    const selectedItem = (layoutItems[level] || []).find(
      (item) => String(item.id || item.itemId) === String(itemId),
    );
    if (selectedItem) {
      setFormData((prev) => ({
        ...prev,
        location: selectedItem.name,
      }));
    }

    if (itemId && siteLayout && siteLayout[`level${nextLevel}`]) {
      if (!layoutItems[nextLevel] || layoutItems[nextLevel].length === 0) {
        try {
          const items = await siteLayoutService.getLevelItems(
            formData.plantId,
            nextLevel,
          );
          setLayoutItems((prev) => ({ ...prev, [nextLevel]: items || [] }));
        } catch (err) {
          console.error(`Error fetching level ${nextLevel} items:`, err);
        }
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setFormData((prev) => ({
        ...prev,
        filesToUpload: [...prev.filesToUpload, ...files],
      }));
    }
  };

  const removeFileToUpload = (index) => {
    setFormData((prev) => {
      const updatedFiles = [...prev.filesToUpload];
      updatedFiles.splice(index, 1);
      return { ...prev, filesToUpload: updatedFiles };
    });
  };

  const validateForm = (isDraft = false) => {
    if (!formData.title?.trim()) {
      toast.error("Hazard Title is required");
      return false;
    }

    // Skip other validations for drafts
    if (isDraft) return true;

    if (!formData.location?.trim() && !formData.plantId) {
      toast.error("Plant/Location is required");
      return false;
    }
    if (!formData.description?.trim()) {
      toast.error("Hazard Description is required");
      return false;
    }
    if (
      settings.requireEvidence &&
      formData.attachments.length === 0 &&
      formData.filesToUpload.length === 0
    ) {
      toast.error("Photographic evidence is required by the settings");
      return false;
    }
    return true;
  };

  const handleSave = async (statusOverride = null) => {
    if (!validateForm(statusOverride === "Draft")) {
      return;
    }

    setSaving(true);
    try {
      const reportedAtDateTime =
        formData.reportedAtDate && formData.reportedAtTime
          ? new Date(`${formData.reportedAtDate}T${formData.reportedAtTime}`)
          : new Date();

      const payload = {
        title: formData.title,
        reporterName: formData.reporterName,
        reportedByUserId: formData.reportedByUserId
          ? parseInt(formData.reportedByUserId)
          : null,
        departmentId: formData.departmentId
          ? parseInt(formData.departmentId)
          : null,
        reportedAt: reportedAtDateTime.toISOString(),
        location: formData.location || "General Area",
        plantId: formData.plantId ? parseInt(formData.plantId) : null,
        description: formData.description,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        isImmediateDanger: formData.isImmediateDanger,
        severityLevelId: formData.severityLevelId
          ? parseInt(formData.severityLevelId)
          : null,
        immediateActionTaken: formData.immediateActionTaken,
        approverRoleId: formData.approverRoleId
          ? parseInt(formData.approverRoleId)
          : null,
        status: statusOverride || formData.status,
      };

      let savedId = reportId;
      if (isEditMode) {
        await hazardReportService.updateHazardReport(reportId, payload);
        toast.success(
          statusOverride === "Draft"
            ? "Draft saved successfully"
            : "Hazard report updated successfully",
        );
      } else {
        const createdReport =
          await hazardReportService.createHazardReport(payload);
        savedId = createdReport.hazardId;
        toast.success(
          statusOverride === "Draft"
            ? "Draft saved successfully"
            : "Hazard report created successfully",
        );
      }

      if (formData.filesToUpload.length > 0) {
        for (const file of formData.filesToUpload) {
          await hazardReportService.uploadAttachment(savedId, file);
        }
      }

      if (onClose) onClose();
    } catch (err) {
      console.error("Error saving report:", err);
      toast.error("Failed to save report.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    await handleSave();
  };

  const handleSaveDraft = async (e) => {
    if (e) e.preventDefault();
    await handleSave("Draft");
  };

  return (
    <>
      <div
        className={`side-drawer-backdrop ${isOpen ? "open" : ""}`}
        onClick={onClose}
      ></div>
      <div className={`side-drawer ${isOpen ? "open" : ""}`}>
        <div
          className="drawer-header"

        >
          <h3>
            {isEditMode ? (
              <span>
                {formData.referenceNumber ||
                  `HZD-${formData.reportedAtDate
                    ? new Date(formData.reportedAtDate).getFullYear()
                    : new Date().getFullYear()
                  }-${String(reportId).padStart(3, "0")}`}
              </span>
            ) : (
              <span>
                Submit Hazard Report
              </span>
            )}
          </h3>
          <div
            className="header-actions"
            style={{ display: "flex", alignItems: "center", gap: "12px" }}
          >
            <button
              className="btn btnsavedraft"
              onClick={handleSaveDraft}
              disabled={saving}

            >
              Save Draft
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving}
              style={{ padding: "10px 24px", height: "44px" }}
            >
              {saving
                ? "Submitting..."
                : isEditMode
                  ? "Update Report"
                  : "Submit Report"}
            </button>
            <button
              className="drawer-close-btn"
              onClick={onClose}
              style={{ width: "40px", height: "40px", borderRadius: "12px" }}
            >
              <FiX />
            </button>
          </div>
        </div>

        <div className="drawer-content">
          {loading ? (
            <PageLoading variant="compact" message="Loading…" />
          ) : (
            <>
              {/* AI INSPECTION SUMMARY BANNER */}
              {aiPrefillObject &&
                (aiPrefillObject.hazardTitle ||
                  aiPrefillObject.hazard_title) && (
                  <div
                    style={{
                      backgroundColor: "#f5f3ff",
                      border: "1px solid #c4b5fd",
                      borderRadius: "12px",
                      padding: "16px 20px",
                      marginBottom: "24px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <h3
                        style={{
                          color: "#5b21b6",
                          margin: 0,
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <HiOutlineSparkles size={20} />
                        AI Inspection Complete
                      </h3>
                      <span
                        style={{
                          backgroundColor: "#ede9fe",
                          color: "#6d28d9",
                          padding: "4px 10px",
                          borderRadius: "20px",
                          fontSize: "12px",
                          fontWeight: "bold",
                          border: "1px solid #ddd6fe",
                        }}
                      >
                        Confidence:{" "}
                        {aiPrefillObject.aiConfidence ||
                          aiPrefillObject.ai_confidence
                          ? Math.round(
                            (aiPrefillObject.aiConfidence ||
                              aiPrefillObject.ai_confidence) * 100,
                          )
                          : "--"}
                        %
                      </span>
                    </div>

                    <p
                      style={{
                        margin: 0,
                        color: "#4c1d95",
                        fontSize: "14px",
                        lineHeight: "1.5",
                      }}
                    >
                      The Smart AI Scan has categorized this issue as a{" "}
                      <strong>
                        {aiPrefillObject.hazardCategory ||
                          aiPrefillObject.hazard_category}
                      </strong>{" "}
                      hazard with a <strong>{aiPrefillObject.severity}</strong>{" "}
                      severity level.
                    </p>

                    {(aiPrefillObject.detectedObjects ||
                      aiPrefillObject.detected_objects) &&
                      (
                        aiPrefillObject.detectedObjects ||
                        aiPrefillObject.detected_objects
                      ).length > 0 && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                            marginTop: "4px",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#6d28d9",
                              fontWeight: "600",
                            }}
                          >
                            Detected Items:
                          </span>
                          {(
                            aiPrefillObject.detectedObjects ||
                            aiPrefillObject.detected_objects
                          ).map((obj, idx) => (
                            <span
                              key={idx}
                              style={{
                                backgroundColor: "#ffffff",
                                color: "#5b21b6",
                                padding: "2px 8px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                border: "1px solid #e2e8f0; border-color: var(--border-primary)",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                              }}
                            >
                              {obj.label} (
                              {Math.round((obj.confidence || 0) * 100)}%)
                            </span>
                          ))}
                        </div>
                      )}
                  </div>
                )}

              <div className="form-section">
                <h3>Reporter Details</h3>
                <div className="form-row">
                  <div className="form-col">
                    <label className="required-label">Plant</label>
                    <select
                      name="plantId"
                      value={formData.plantId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Please Select Plant
                      </option>
                      {plants.map((p) => (
                        <option key={p.plantId} value={p.plantId}>
                          {p.plantName || p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-col">
                    <label>Department</label>
                    <select
                      name="departmentId"
                      value={formData.departmentId}
                      onChange={handleChange}
                      disabled={!formData.plantId}
                    >
                      <option value="" disabled>
                        Select Department
                      </option>
                      {departments.map((d) => (
                        <option key={d.departmentId} value={d.departmentId}>
                          {d.departmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-col">
                    <label className="required-label">
                      Hazard Title :
                      {aiSuggested.title && (
                        <span className="ai-badge">✨ AI Suggested</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Please enter a brief title for this hazard"
                      value={formData.title}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-col">
                    <label className="required-label">Reporter Name</label>
                    <input
                      type="text"
                      name="reporterName"
                      placeholder="Please provide the name of the person reporting"
                      value={formData.reporterName}
                      disabled
                      style={{
                        backgroundColor: "#f8fafc",
                        cursor: "not-allowed",
                        color: "#94a3b8",
                        borderColor: "#e2e8f0",
                      }}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-col">
                    <label>Reporting Date :</label>
                    <input
                      type="date"
                      name="reportedAtDate"
                      value={formData.reportedAtDate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-col">
                    <label>Reporting Time :</label>
                    <input
                      type="time"
                      name="reportedAtTime"
                      value={formData.reportedAtTime}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {settings.enableApproval && (
                  <div className="form-row">
                    <div className="form-col" style={{ maxWidth: "33.33%" }}>
                      <label>Send for Approval To :</label>
                      <select
                        name="approverRoleId"
                        value={formData.approverRoleId}
                        onChange={handleChange}
                      >
                        <option value="">No Approval Required</option>
                        {roles.map((r) => (
                          <option key={r.roleId} value={r.roleId}>
                            {r.roleName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-row full-width">
                  <div className="form-col">
                    <label className="required-label">
                      Hazard Description :
                      {aiSuggested.description && (
                        <span className="ai-badge">✨ AI Suggested</span>
                      )}
                    </label>
                    <textarea
                      name="description"
                      placeholder="Please describe the observed hazard in detail"
                      value={formData.description}
                      onChange={handleChange}
                      style={{
                        minHeight: "80px",
                        resize: "vertical",
                        width: "100%",
                        border: "1px solid #cbd5e1",
                        borderRadius: "12px",
                        padding: "10px 16px",
                        fontSize: "14px",
                        color: "#0f172a",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  Hazard Location
                  <button
                    type="button"
                    className="btn-icon-only"
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#e0e7ff",
                      color: "#4f46e5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "none",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      if (onClose) onClose();
                      navigate("/home/settings/site-layout");
                    }}
                    title="Configure Site Layout"
                  >
                    <FiPlus size={16} />
                  </button>
                </h3>

                <div className="form-row" style={{ width: "100%" }}>
                  {!formData.plantId ? (
                    <div
                      style={{
                        width: "100%",
                        gridColumn: "1 / -1",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "20px",
                        border: "2px dashed #e2e8f0; border-color: var(--border-primary)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <div
                        style={{
                          width: "56px",
                          height: "56px",
                          borderRadius: "18px",
                          backgroundColor: "#eff6ff",
                          color: "#3b82f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "16px",
                          boxShadow: "0 4px 12px rgba(59, 130, 246, 0.1)",
                        }}
                      >
                        <FiInfo size={28} />
                      </div>
                      <span
                        style={{
                          color: "#1e293b",
                          fontSize: "1.1rem",
                          fontWeight: "700",
                          marginBottom: "4px",
                        }}
                      >
                        Selection Required
                      </span>
                      <span
                        style={{
                          color: "#64748b",
                          fontSize: "0.9rem",
                          textAlign: "center",
                          maxWidth: "300px",
                          lineHeight: "1.5",
                        }}
                      >
                        Please select a <b>Plant</b> above to see the available
                        facility locations and levels.
                      </span>
                    </div>
                  ) : siteLayout && siteLayout.level1 ? (
                    [1, 2, 3, 4, 5, 6].map((level) => {
                      const levelName = siteLayout[`level${level}`];
                      if (!levelName) return null;

                      const allLevelItems = layoutItems[level] || [];
                      let items = allLevelItems;

                      if (level > 1) {
                        const parentId = selectedLayout[level - 1];
                        if (!parentId) {
                          items = [];
                        } else {
                          items = allLevelItems.filter(
                            (item) =>
                              String(item.parentId) === String(parentId),
                          );
                        }
                      }

                      // Apply facilityLevel limit from settings
                      if (level > settings.facilityLevel) return null;

                      const isDisabled =
                        level > 1 && !selectedLayout[level - 1];

                      return (
                        <div
                          className="form-col"
                          key={level}
                          style={{ minWidth: "200px" }}
                        >
                          <label>{levelName}</label>
                          <select
                            value={selectedLayout[level] || ""}
                            onChange={(e) =>
                              handleLayoutChange(level, e.target.value)
                            }
                            disabled={isDisabled}
                          >
                            <option value="">Select {levelName}</option>
                            {items.map((item) => (
                              <option
                                key={item.id || item.itemId}
                                value={item.id || item.itemId}
                              >
                                {item.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    })
                  ) : null}
                </div>

                <div className="form-row" style={{ marginTop: "15px" }}>
                  <div className="form-col">
                    <label
                      className={!formData.plantId ? "required-label" : ""}
                    >
                      Specific Location Title :
                    </label>
                    <input
                      type="text"
                      name="location"
                      placeholder="Please specify the exact location where the hazard was found"
                      value={formData.location}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Categorization & Assessment</h3>
                <div className="form-row">
                  <div className="form-col">
                    <label>Hazard Category</label>
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleChange}
                    >
                      <option value="" disabled>
                        Select Category
                      </option>
                      {categories.map((c) => (
                        <option
                          key={c.categoryId || c.CategoryId || c.id}
                          value={c.categoryId || c.CategoryId || c.id}
                        >
                          {c.categoryName || c.CategoryName || c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {settings.enableSeverity && (
                    <div className="form-col">
                      <label>Initial Severity Estimate</label>
                      <select
                        name="severityLevelId"
                        value={formData.severityLevelId}
                        onChange={handleChange}
                      >
                        <option value="" disabled>
                          Select Severity
                        </option>
                        {severityLevels.map((s) => (
                          <option
                            key={s.severityLevelId || s.SeverityLevelId || s.id}
                            value={
                              s.severityLevelId || s.SeverityLevelId || s.id
                            }
                          >
                            {s.name || s.Name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                {settings.enableImmediateDangerTracking && (
                  <div
                    className="form-row"
                    style={{ alignItems: "center", marginTop: "16px" }}
                  >
                    <label className="switch-styled">
                      <input
                        type="checkbox"
                        id="isImmediateDanger"
                        name="isImmediateDanger"
                        checked={formData.isImmediateDanger}
                        onChange={handleChange}
                      />
                      <span className="slider-styled"></span>
                    </label>
                    <label
                      htmlFor="isImmediateDanger"
                      className="toggle-label-text"
                    >
                      Mark as Immediate Danger / Urgency
                    </label>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3>Evidence & Response</h3>

                <div className="form-row">
                  <div className="form-col" style={{ flex: "100%" }}>
                    <label
                      className={
                        settings.requireEvidence ? "required-label" : ""
                      }
                    >
                      Upload Evidence (Photos/Videos)
                    </label>
                    <div
                      style={{
                        padding: "20px",
                        border: "2px dashed #e2e8f0; border-color: var(--border-primary)",
                        borderRadius: "16px",
                        backgroundColor: "#fcfdfe",
                        textAlign: "center",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        marginBottom: "16px",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.borderColor = "#6366f1")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.borderColor = "#e2e8f0")
                      }
                    >
                      <input
                        type="file"
                        id="evidenceUpload"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                      <label
                        htmlFor="evidenceUpload"
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "8px",
                          color: "#64748b",
                        }}
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "14px",
                            backgroundColor: "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#6366f1",
                            marginBottom: "4px",
                          }}
                        >
                          <HiOutlinePhoto size={24} />
                        </div>
                        <span
                          style={{
                            fontWeight: "700",
                            color: "#1e293b",
                            fontSize: "14px",
                          }}
                        >
                          Click to upload evidence
                        </span>
                        <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                          Images or videos up to 10MB
                        </span>
                      </label>
                    </div>
                    {/* AI analyze button removed as per user request */}

                    {isEditMode &&
                      formData.attachments &&
                      formData.attachments.length > 0 && (
                        <div style={{ marginTop: "10px" }}>
                          <small style={{ fontWeight: "bold" }}>
                            Existing Evidences:
                          </small>
                          <div
                            style={{
                              display: "flex",
                              gap: "10px",
                              flexWrap: "wrap",
                              marginTop: "5px",
                            }}
                          >
                            {formData.attachments.map((file, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: "5px 10px",
                                  backgroundColor: "#e2e8f0",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                <a
                                  href={file.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    textDecoration: "none",
                                    color: "#333",
                                  }}
                                >
                                  {file.fileName}
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {formData.filesToUpload.length > 0 && (
                      <div style={{ marginTop: "10px" }}>
                        <small style={{ fontWeight: "bold" }}>
                          To be uploaded:
                        </small>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                            marginTop: "5px",
                          }}
                        >
                          {formData.filesToUpload.map((file, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "5px 10px",
                                backgroundColor: "#e0f2fe",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              <span>{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFileToUpload(idx)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "#dc2626",
                                  cursor: "pointer",
                                  padding: "0",
                                }}
                              >
                                <FiX size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {settings.enableImmediateAction && (
                  <div className="form-row full-width">
                    <div className="form-col">
                      <label>
                        Immediate Action Taken (Optional)
                        {aiSuggested.immediateActionTaken && (
                          <span className="ai-badge">✨ AI Suggested</span>
                        )}
                      </label>
                      <textarea
                        name="immediateActionTaken"
                        placeholder="Please detail any immediate steps taken to mitigate the risk"
                        value={formData.immediateActionTaken}
                        onChange={handleChange}
                        style={{
                          minHeight: "80px",
                          resize: "vertical",
                          width: "100%",
                          border: "1px solid #cbd5e1",
                          borderRadius: "12px",
                          padding: "10px 16px",
                          fontSize: "14px",
                          color: "#0f172a",
                          outline: "none",
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default HazardReportForm;
